import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Presentation, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PPT_FILE_URL = 'https://customer-assets.emergentagent.com/job_97725229-7456-4625-a973-72749269d95f/artifacts/6a8kvw9e_HR%20DEPARTMENT%20PPT.pptx';

const SLIDES = [
  {
    title: 'INTRODUCTION',
    body: [
      'Human Resource Department is the focal point of any business establishment or organization.',
      'Various functions and departments come under the umbrella of manpower.',
      'The HR department works to fulfil the role of recruiting, training and managing employees in a company.',
      'In this presentation, we discuss the functions, roles, structures, skills, activities and importance of HR in the company.',
    ],
  },
  {
    title: 'ROLE OF HUMAN RESOURCE IN ORGANISATION',
    body: [
      '"Human Resources" (HR) refers to all the people who contribute to an organization through their work.',
      'This includes regular employees, independent contractors, interns and outsourced companies.',
      'HR carries out employment-related activities — managing the labor lifecycle: hiring, interviewing, retaining, training and terminating.',
      'HR also assesses employee performance and determines remuneration and benefits.',
      'The role of HR has evolved due to globalization and shifts in the labor market.',
    ],
  },
  {
    title: 'FUNCTIONS OF HR DEPARTMENT',
    body: [
      'Recruitment & Selection',
      'Training & Development',
      'Performance Management',
      'Compensation & Benefits',
      'Employee Engagement & Welfare',
      'Compliance & Policy enforcement',
    ],
  },
  {
    title: 'MAJOR ACTIVITIES OF HR DEPARTMENT',
    body: [
      'Talent acquisition and onboarding',
      'Attendance, leave & shift management',
      'Salary processing & statutory compliance',
      'Internal communication & circulars',
      'Grievance handling & disciplinary action',
      'Reward & recognition programmes',
    ],
  },
  {
    title: 'TOP 15 HR SKILLS',
    body: [
      'Communication','Decision making','Conflict resolution',
      'Empathy','Multitasking','Active listening',
      'Confidentiality','Adaptability','Coaching & mentoring',
      'Leadership','Time management','Negotiation',
      'Analytical thinking','Recruiting','Technological literacy',
    ],
  },
  {
    title: 'HR JOB HIERARCHY',
    body: [
      'HR departments often build a hierarchy of jobs, each containing employees who support others or perform specific duties.',
      'Lower / entry-level HR jobs',
      'Mid-level HR jobs',
      'Senior-level HR jobs',
      'Specialised HR positions',
    ],
  },
  {
    title: 'QUALITIES OF GOOD HR',
    body: [
      'Ability to prioritise and switch between tasks efficiently',
      'Problem-solving skills — keep employees motivated',
      'Risk-taking ability — learn from past mistakes',
      'Approachability — comfortable to all levels of employees',
      'Strong ethics — protect the company\'s sensitive data',
    ],
  },
  {
    title: 'WHY HUMAN RESOURCES IS IMPORTANT?',
    body: [
      'Aligns workforce with company goals',
      'Maintains organizational culture & morale',
      'Drives productivity through training & development',
      'Ensures legal & regulatory compliance',
      'Acts as bridge between management and employees',
    ],
  },
  {
    title: 'DEPARTMENTS',
    body: ['MARKETING','FINANCE','HR','LEGAL','OPERATION','IT'],
  },
  {
    title: 'ARCHITECT HR FORMAT',
    body: [
      'Standardised templates across the organization',
      'Consistent letter / form / report design',
      'Centralised forms library',
    ],
  },
  {
    title: 'SOURCES OF RECRUITMENT',
    body: [
      'Application form','First interview','Second interview',
      'Offer letter','Joining letter','Joining form',
      'Appointment letter','Probation letter','Award & Reward',
      'Job confirmation letter','Employee agreement','ID card','Visiting card',
    ],
  },
  {
    title: 'HR SOP',
    body: [
      'HR Circular','Leave application','Job promotion letter',
      'Salary advance letter','Salary advance form','Apolization letter',
      'Salary slip','Resignation letter','Experience letter','Exit interview letter',
    ],
  },
  {
    title: 'HR DEPARTMENT REPORT',
    body: [
      'Presenty report','Work-of-all-staff schedule','All staff work report',
      'CTC report','Performance report','Setup report','Skill report',
      'Salary calculation','Leave planning report','Company hierarchy chart',
      'Weekly work','Leave planning','New agreement','Staff training',
      'Staff performance checking','File checking',
    ],
  },
  {
    title: 'DEPARTMENT REQUIREMENTS',
    body: [
      'Marketing department requirement','Finance department requirement','Legal department requirement',
      'IT department requirement','Operation department requirement','Office environment',
      'All project work follow up','Field staff work auditing','Staff dress code checking',
      'Monthly meeting arrangement','Monthly report',
    ],
  },
  {
    title: 'NEW STAFF REQUIREMENT',
    body: [
      'Staff resign processing','Salary calculation','Dress code checking',
      'Staff pendrive checking','Desk top / laptop file checking','Daily work file printing',
    ],
  },
  {
    title: 'STANDARD OPERATING PROCEDURE',
    body: [
      'MORNING SCHEDULE — DBK group thinking project-wise; presently status checking; calling to staff; leave planning; all staff work follow-up; staff meeting; departmental requirements; report to senior; work planning & follow-up; staff training; penalty confirmation.',
      'AFTERNOON SCHEDULE — Greeting & shouting calls; new recruitment & requirement; recruitment sources development; performance measurement; penalty calling & salary confirmation; CTC call & marketing communication; evening follow-up with all staff; senior report; closing.',
    ],
  },
  {
    title: 'THANK YOU',
    body: ['HR DEPARTMENT — MR. SAHIL KARALE'],
  },
];

const SLIDE_THEMES = [
  'from-blue-700 to-blue-500',
  'from-emerald-700 to-emerald-500',
  'from-amber-700 to-amber-500',
  'from-rose-700 to-rose-500',
  'from-indigo-700 to-indigo-500',
  'from-teal-700 to-teal-500',
  'from-purple-700 to-purple-500',
  'from-orange-700 to-orange-500',
];

export default function PresentationPPTTab() {
  const [active, setActive] = useState(0);
  const slide = SLIDES[active];
  const theme = SLIDE_THEMES[active % SLIDE_THEMES.length];

  return (
    <div className="space-y-4" data-testid="hr-ppt-tab">
      <div className="flex items-center justify-between bg-purple-900 text-white px-4 py-3 rounded-lg">
        <div className="flex items-center gap-3">
          <Presentation className="w-5 h-5" />
          <div>
            <h2 className="text-lg font-bold">HR DEPARTMENT — Presentation</h2>
            <p className="text-xs text-purple-200">{SLIDES.length} slides · Mr. Sahil Karale</p>
          </div>
        </div>
        <a href={PPT_FILE_URL} target="_blank" rel="noopener noreferrer" download>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ppt-download-btn">
            <Download className="w-4 h-4 mr-1.5" /> Download .pptx
          </Button>
        </a>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Slide List */}
        <Card className="col-span-12 md:col-span-3 max-h-[640px] overflow-auto">
          <CardContent className="p-2 space-y-1">
            {SLIDES.map((s, i) => (
              <button
                key={i}
                data-testid={`slide-thumb-${i+1}`}
                onClick={() => setActive(i)}
                className={`w-full text-left px-3 py-2 rounded text-xs transition-colors ${active === i ? 'bg-purple-100 border-l-4 border-purple-700 text-purple-900 font-bold' : 'hover:bg-stone-100 text-stone-700'}`}
              >
                <span className="text-[10px] text-stone-400 mr-1.5">{String(i+1).padStart(2, '0')}</span>
                {s.title}
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Slide Content */}
        <Card className="col-span-12 md:col-span-9">
          <CardContent className="p-0">
            <div className={`bg-gradient-to-br ${theme} text-white p-8 min-h-[480px] rounded-t-lg flex flex-col`}>
              <div className="flex items-center gap-3 mb-6 opacity-80">
                <div className="bg-white/20 px-3 py-1 rounded-full text-xs font-bold">SLIDE {active + 1} / {SLIDES.length}</div>
              </div>
              <h2 className="text-3xl font-bold mb-6 leading-tight">{slide.title}</h2>
              <div className="flex-1 space-y-3 text-base">
                {slide.body.map((b, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="text-2xl leading-none opacity-60">▸</span>
                    <span className="leading-relaxed">{b}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-b-lg">
              <Button
                variant="outline"
                size="sm"
                disabled={active === 0}
                onClick={() => setActive(a => Math.max(0, a - 1))}
                data-testid="ppt-prev"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <span className="text-xs text-stone-500">{active + 1} of {SLIDES.length}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={active === SLIDES.length - 1}
                onClick={() => setActive(a => Math.min(SLIDES.length - 1, a + 1))}
                data-testid="ppt-next"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
